import React, { useEffect, useState } from 'react';
import { Provider } from 'models';
import { Grid } from '@material-ui/core';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { CalendarPicker, PrimaryButton } from 'library';
import BookFromCalendarModal from './BookFromCalendarModal';
import { client, track } from 'shared';
import JustBookedModal from './JustBookedModal';
import { useUserContext } from 'UserContext';
import { hasNoAvailibityForDate } from './Utils';
import { isLoggedIn } from 'api/UserApi';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import ConnectPaymentModal from 'parent-portal/payments/Components/ConnectPaymentModal';
import { PickerOnChangeFn } from '@mui/lab/internal/pickers/hooks/useViews';
import usePublicSessionTracking, { PublicSessionEventType } from 'parent-portal/shared/PublicSessionTracking';
import useCookies from 'shared/Cookies';

export interface Availability {
    date: Date;
    t_06_09: boolean;
    t_09_12: boolean;
    t_12_15: boolean;
    t_15_18: boolean;
    t_18_21: boolean;
    t_21_24: boolean;
    user_id: number;
}
type ProfileParams = {
    id: string;
};

export default function CalendarAvailability({
    provider,
    quickBook = false,
}: {
    provider?: Provider;
    quickBook?: boolean;
}) {
    const [availability, setAvailability] = useState<any>();
    const [showBookFromCalendarModal, setShowBookFromCalendarModal] = useState(false);
    const [showJustBookedModal, setShowJustBookedModal] = useState(false);
    const [showPaymentSetup, setShowPaymentSetup] = useState(false);
    const { user } = useUserContext();
    const { setCookie } = useCookies(['provider_accessor']);
    const { id } = useParams<ProfileParams>();
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const history = useHistory();

    const { trackSession } = usePublicSessionTracking('Provider Availability Calendar');

    function setAvailabilityFromServer(availability: Availability[]) {
        let transformedAvailability: any = {};
        for (let i = 0; i < availability.length; i++) {
            let dateStr = availability[i].date;
            let calDate = new Date(dateStr + 'T10:00:00.000Z');
            calDate.setHours(0, 0, 0, 0);
            let key = calDate.getTime();
            transformedAvailability[key] = {};
            transformedAvailability[key]['t_06_09'] = availability[i].t_06_09;
            transformedAvailability[key]['t_09_12'] = availability[i].t_09_12;
            transformedAvailability[key]['t_12_15'] = availability[i].t_12_15;
            transformedAvailability[key]['t_15_18'] = availability[i].t_15_18;
            transformedAvailability[key]['t_18_21'] = availability[i].t_18_21;
            transformedAvailability[key]['t_21_24'] = availability[i].t_21_24;
        }
        setAvailability(transformedAvailability);
    }

    useEffect(() => {
        setShowBookFromCalendarModal(false);
        if (provider) {
            client(`api/availability-enhanced/?user_id=${provider.user.id}`)
                .then(setAvailabilityFromServer)
                .catch((error) => {
                    console.log(error);
                });
        }
    }, []);

    function bookClick() {
        if (user?.claimed && isLoggedIn() && user?.payment_information?.length > 0) {
            setShowBookFromCalendarModal(true);
        } else if (user?.claimed && isLoggedIn()) {
            setShowPaymentSetup(true);
        } else {
            setCookie('provider_accessor', id, { path: '/' });
            history.push('/signup/job');
        }
    }

    function onDateChanged(newValue: Date) {
        newValue.setHours(0, 0, 0, 0);
        setDateValue(newValue as Date);
        if (quickBook) {
            track('Quick Book: Show Modal');
        } else {
            track('Hard Availability: Show Modal', { providerId: provider?.id });
        }
        if (user?.claimed && isLoggedIn() && user?.payment_information?.length > 0) {
            setShowBookFromCalendarModal(true);
        } else if (user?.claimed && isLoggedIn()) {
            setShowPaymentSetup(true);
        } else {
            trackSession('CLICK', 'Clicked date on availability calendar', PublicSessionEventType.User, {
                providerId: provider?.id,
            });
            history.push('/signup/job');
        }
    }

    function onMonthChange(newValue: Date) {
        if (!user) {
            trackSession('CLICK', 'Changed month', PublicSessionEventType.User, {
                providerId: provider?.id,
                date: newValue,
            });
        }
    }

    return (
        <>
            <BookFromCalendarModal
                visible={showBookFromCalendarModal}
                setVisible={setShowBookFromCalendarModal}
                setShowSuccessDialog={setShowJustBookedModal}
                provider={provider}
                dateValue={dateValue}
                setDateValue={setDateValue}
                availability={availability}
                quickBook={quickBook}
            />
            <JustBookedModal
                quickBook={quickBook}
                visible={showJustBookedModal}
                onClose={() => setShowJustBookedModal(false)}
                providerName={provider?.user.first_name ?? ''}
            />
            <Grid container justify="center" style={{ marginTop: 20 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CalendarPicker
                        shouldDisableDate={(date) => {
                            return hasNoAvailibityForDate(date as Date, availability);
                        }}
                        date={dateValue}
                        views={['day']}
                        onChange={onDateChanged as PickerOnChangeFn<unknown>}
                        onMonthChange={onMonthChange as PickerOnChangeFn<unknown>}
                    />
                </LocalizationProvider>
            </Grid>
            {!quickBook ? (
                <Grid container justify="center">
                    <PrimaryButton buttonStyle={{ maxWidth: '50%' }} onClick={bookClick} disabled={!availability}>
                        Book
                    </PrimaryButton>
                </Grid>
            ) : null}
            <ConnectPaymentModal
                isOpen={showPaymentSetup}
                onClose={() => {
                    track('Payment Modal Closed: Instant Availability');
                    setShowPaymentSetup(false);
                }}
                onSuccess={() => {
                    track('Payment Modal Closed: Instant Availability');
                    setShowBookFromCalendarModal(true);
                }}
                fromRoute={'Instant Availability'}
                subtitle="Payment setup required for direct booking."
            />
        </>
    );
}
